"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

import { BOTON_PRIMARIO, CAMPO } from "@/components/shared/estilos";
import {
  AccesoApiError,
  solicitarCodigo,
  URL_PRIVACIDAD,
  URL_TERMINOS,
  type CodigoVerificado,
  type ErrorGoogle,
} from "@/lib/acceso";
import { getCurrentUser } from "@/lib/auth";
import { paisDeLocale } from "@/lib/billing";
import { NOMBRE_COMPLETO } from "@/lib/marca";
import { listAccessibleOrganizations } from "@/lib/organizations";
import { setCurrentOrganizationCookie } from "@/lib/tenant-context";

import type { InvitacionAcceso } from "./cargarAcceso";
import BotonGoogle from "./BotonGoogle";
import LoginContrasenaForm from "./LoginContrasenaForm";
import PasoCodigo from "./PasoCodigo";
import Spinner from "./Spinner";
import { ENLACE_TACTIL } from "./estilosAcceso";

type Paso = "correo" | "codigo" | "dosFactores" | "contrasena";
type Idioma = "es" | "en" | "de";

const IDIOMAS: ReadonlySet<string> = new Set<Idioma>(["es", "en", "de"]);
const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// El borde rojo por `aria-invalid`: la variante va detrás en la hoja de
// estilos y gana al `border-line-strong` de `CAMPO`.
const CAMPO_CORREO = [CAMPO, "aria-[invalid=true]:border-red-500"].join(" ");
const TITULAR =
  "text-center text-2xl font-bold tracking-tight text-balance text-zinc-950 sm:text-3xl";
const ENLACE_TERMINOS = "underline underline-offset-2 hover:text-zinc-900";

export interface AccesoClientProps {
  modo: "entrar" | "empezar";
  /** Precio mensual de la plataforma ya formateado; `null` quita el importe de la línea. */
  errorInicial?: ErrorGoogle | null;
  /** Precargado cuando Google devuelve `google_2fa`. */
  emailInicial?: string | null;
  /** Ruta propia a la que ir tras entrar (`?redirect=`), ya validada. */
  destino?: string | null;
  /** Abrir en la variante con contraseña (`?modo=contrasena`). */
  conContrasena?: boolean;
  /** Invitación de alta válida (`/start?invite=`, 030-lite). */
  invitacion?: InvitacionAcceso | null;
  /** Llegó `?invite=` pero ha caducado o ya se usó: aviso y pantalla normal. */
  invitacionInvalida?: boolean;
}

/**
 * La puerta del producto (UI spec 003, S1–S1-d): una sola pantalla para quien
 * entra y para quien empieza, porque es el backend (`entrar_o_crear`) quien
 * decide. Lleva la máquina de pasos `correo` → `codigo` → `dosFactores`, más
 * `contrasena`; el correo se conserva entre pasos.
 */
export default function AccesoClient({
  modo,
  errorInicial = null,
  emailInicial = null,
  destino = null,
  conContrasena = false,
  invitacion = null,
  invitacionInvalida = false,
}: AccesoClientProps) {
  const t = useTranslations("auth.acceso");
  const tLogin = useTranslations("auth.login");
  const locale = useLocale();
  const idioma = IDIOMAS.has(locale) ? (locale as Idioma) : undefined;
  // País del navegador (spec 022 §21.5) para el código y Google. No se pinta:
  // en el servidor es `null` y en el cliente ya es el del navegador.
  const [pais] = useState(() =>
    typeof navigator === "undefined" ? null : paisDeLocale(navigator.language)
  );

  const [paso, setPaso] = useState<Paso>(conContrasena ? "contrasena" : "correo");
  const [email, setEmail] = useState(emailInicial ?? "");
  const [codigoCorreo, setCodigoCorreo] = useState("");
  const [errorCampo, setErrorCampo] = useState<string | null>(null);
  const token = invitacion?.token ?? null;
  const [error, setError] = useState<string | null>(() => {
    if (invitacionInvalida) return t("errores.invitacionInvalida");
    switch (errorInicial) {
      case "google_cancelado":
        return t("errores.googleCancelado");
      case "google_fallo":
        return t("errores.googleFallo");
      case "google_2fa":
        return t("errores.google2fa");
      case "cuenta_suspendida":
        return tLogin("errors.suspended");
      case "correo_temporal":
        return t("errores.correoTemporal");
      case "alta_restringida":
        return t("errores.altaRestringida");
      case "invitacion_invalida":
        return t("errores.invitacionInvalida");
      case "invitacion_otro_correo":
        return t("errores.invitacionOtroCorreo");
      default:
        return null;
    }
  });
  const [enviando, setEnviando] = useState(false);
  const [enfocarCorreo, setEnfocarCorreo] = useState(false);

  const correoRef = useRef<HTMLInputElement>(null);

  // Al volver de S1-b («Cambiar el correo») el foco va al correo, que se conserva.
  useEffect(() => {
    if (paso === "correo" && enfocarCorreo) {
      correoRef.current?.focus();
      setEnfocarCorreo(false);
    }
  }, [paso, enfocarCorreo]);

  function volverAlCorreo(): void {
    setCodigoCorreo("");
    setError(null);
    setPaso("correo");
    setEnfocarCorreo(true);
  }

  async function pedirCodigo(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const limpio = email.trim().toLowerCase();
    const errorValidacion = !limpio
      ? tLogin("validation.emailRequired")
      : !EMAIL_VALIDO.test(limpio)
        ? tLogin("validation.emailInvalid")
        : null;
    setErrorCampo(errorValidacion);
    if (errorValidacion) {
      correoRef.current?.focus();
      return;
    }

    setEnviando(true);
    try {
      await solicitarCodigo(limpio, idioma, token);
      // Siempre avanza: el backend responde igual exista o no la cuenta.
      setEmail(limpio);
      setPaso("codigo");
    } catch (err) {
      if (err instanceof AccesoApiError && err.code === "EMAIL_DISPOSABLE") {
        // Feature 026, 2.ª tanda: un error del propio campo, junto al correo.
        setErrorCampo(t("errores.correoTemporal"));
      } else if (err instanceof AccesoApiError && err.code === "SIGNUP_RESTRICTED") {
        setErrorCampo(t("errores.altaRestringida"));
      } else if (err instanceof AccesoApiError && err.code === "SIGNUP_INVITATION_EMAIL_MISMATCH") {
        setErrorCampo(t("errores.invitacionOtroCorreo"));
      } else if (err instanceof AccesoApiError && err.code === "SIGNUP_INVITATION_INVALID") {
        setError(t("errores.invitacionInvalida"));
      } else {
        setError(
          err instanceof AccesoApiError
            ? err.code === "RATE_LIMITED"
              ? t("errores.demasiadosCodigos")
              : tLogin("errors.generic")
            : tLogin("errors.connectionFailed")
        );
      }
      correoRef.current?.focus();
    } finally {
      setEnviando(false);
    }
  }

  /** Como el login de siempre: local actual en la cookie y navegación entera. */
  async function entrar(resultado: CodigoVerificado): Promise<void> {
    try {
      const orgId = resultado.org_id ?? (await listAccessibleOrganizations())[0]?.id;
      if (orgId) setCurrentOrganizationCookie(orgId);
    } catch {
      // Best effort: el layout de la app resuelve el local si falta la cookie.
    }
    // Cuenta recién creada (con su empresa nueva): directa a la puesta en marcha.
    let objetivo = destino ?? (resultado.nuevo ? "/setup" : "/dashboard");
    if (resultado.must_reset_password) {
      objetivo = "/force-password-reset";
    } else if (!destino) {
      const usuario = await getCurrentUser();
      if (usuario?.role === "support") objetivo = "/support";
    }
    // Navegación de documento: la siguiente petición ya lleva la cookie de sesión.
    window.location.assign(objetivo);
  }

  if (paso === "codigo" || paso === "dosFactores") {
    return (
      <PasoCodigo
        key={paso}
        tipo={paso === "codigo" ? "correo" : "dosFactores"}
        email={email}
        codigoCorreo={codigoCorreo}
        idioma={idioma}
        pais={pais}
        invitacion={token}
        onVolver={volverAlCorreo}
        onDosFactores={(codigo) => {
          setCodigoCorreo(codigo);
          setPaso("dosFactores");
        }}
        onEntrar={entrar}
      />
    );
  }

  const titulo =
    modo === "empezar"
      ? t("tituloEmpezar", { marca: NOMBRE_COMPLETO })
      : t("tituloEntrar", { marca: NOMBRE_COMPLETO });

  if (paso === "contrasena") {
    return (
      <>
        <h1 className={[TITULAR, "mb-6"].join(" ")}>{titulo}</h1>
        {/* El error de la URL (Google devuelve aquí al equipo de la plataforma
            con `google_fallo`) va dentro del formulario, que lo quita al enviar. */}
        <LoginContrasenaForm emailInicial={email.trim()} errorInicial={error} />
        <div className="mt-6 flex flex-col items-center gap-3">
          <Link href="/forgot-password" className={ENLACE_TACTIL}>
            {tLogin("forgotPassword")}
          </Link>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setPaso("correo");
            }}
            className={ENLACE_TACTIL}
          >
            {t("sinContrasena")}
          </button>
        </div>
      </>
    );
  }

  const idCorreo = "acceso-correo";
  const idErrorCorreo = "acceso-correo-error";

  return (
    <>
      <div className="mb-6">
        {invitacion?.nombreLocal ? (
          // Con logo, a la izquierda del texto y centrados los dos; en un móvil estrecho el texto salta de línea a su lado.
          <p className="mb-3 flex items-center justify-center gap-2 text-center text-sm font-medium text-brand">
            {invitacion.logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={invitacion.logoUrl}
                alt={t("invitacionLogoAlt", { nombre: invitacion.nombreLocal })}
                className="h-8 w-8 shrink-0 rounded-lg border border-line bg-white object-contain p-0.5"
              />
            ) : null}
            <span>{t("invitacionPara", { nombre: invitacion.nombreLocal })}</span>
          </p>
        ) : null}
        <h1 className={TITULAR}>{titulo}</h1>
      </div>

      <div className="space-y-4">
        <BotonGoogle siguiente={destino} idioma={idioma} pais={pais} invitacion={token} />

        <div className="flex items-center gap-3 text-xs font-medium tracking-wide text-zinc-400 uppercase">
          <span aria-hidden="true" className="h-px flex-1 bg-line" />
          {t("separador")}
          <span aria-hidden="true" className="h-px flex-1 bg-line" />
        </div>

        <form onSubmit={(e) => void pedirCodigo(e)} noValidate className="space-y-4">
          <div>
            <label htmlFor={idCorreo} className="mb-1.5 block text-sm font-medium text-zinc-700">
              {t("correoLabel")}
            </label>
            <input
              ref={correoRef}
              id={idCorreo}
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("correoPlaceholder")}
              aria-invalid={!!errorCampo}
              aria-describedby={errorCampo ? idErrorCorreo : undefined}
              disabled={enviando}
              className={CAMPO_CORREO}
            />
            {errorCampo && (
              <p id={idErrorCorreo} className="mt-1.5 text-sm text-red-700">
                {errorCampo}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={enviando}
            aria-busy={enviando}
            className={[BOTON_PRIMARIO, "w-full gap-2"].join(" ")}
          >
            {enviando && <Spinner />}
            {enviando ? t("enviando") : t("continuarCorreo")}
          </button>
        </form>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <p className="text-center text-xs text-zinc-500">
          {t.rich("terminos", {
            terminos: (chunks) =>
              URL_TERMINOS ? (
                <a href={URL_TERMINOS} target="_blank" rel="noopener noreferrer" className={ENLACE_TERMINOS}>
                  {chunks}
                </a>
              ) : (
                chunks
              ),
            privacidad: (chunks) =>
              URL_PRIVACIDAD ? (
                <a href={URL_PRIVACIDAD} target="_blank" rel="noopener noreferrer" className={ENLACE_TERMINOS}>
                  {chunks}
                </a>
              ) : (
                chunks
              ),
          })}
        </p>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setPaso("contrasena");
            }}
            className={ENLACE_TACTIL}
          >
            {t("conContrasena")}
          </button>
        </div>
      </div>
    </>
  );
}
